import { useClient } from '@composables/use-clients';
import {
  GetRequestArgsType,
  PostRequestArgsType,
  DeleteRequestArgsType,
} from '@api/v1/types';
import { useTenantStore } from '@stores/tenant-store';
import { urls } from '@api/urls';
import { withTryCatch } from '@api/v1/error-handlers';

export type NewSkillTreeSerializerType = {
  name: string;
  inputFile: File;
};

export type GetSkillTreesArgsType = GetRequestArgsType & {
  skillTreeCode?: string | null;
};

export type GetTenantUserSkillTreesArgsType = GetRequestArgsType & {
  tenantUserId: number;
};

export type GetTenantUserBacklogSkillTreesArgsType = GetRequestArgsType & {
  tenantUserId: number;
};

export type PostNewSkillTreePayloadType = NewSkillTreeSerializerType;

export type DeleteSkillTreeArgsType = DeleteRequestArgsType & {
  skillTreeCode: string;
};

export const apiFetchSkillTrees = async ({
  domain = useTenantStore().domain,
  skillTreeCode = null,
  query = {},
  errorConfig = {},
  callbacks = {},
}: GetSkillTreesArgsType = {}) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.skillTrees({ domain, skillTreeCode }),
      query,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiCreateSkillTree = async ({
  domain = useTenantStore().domain,
  payload,
  errorConfig = {},
  callbacks = {},
}: PostRequestArgsType<PostNewSkillTreePayloadType>) => {
  const impl = async () => {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('inputFile', payload.inputFile);

    const { client } = useClient('auth');
    const res = await client.post({
      url: urls.v1.newSkillTree({ domain }),
      payload: formData,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiFetchTenantUserSkillTrees = async ({
  domain = useTenantStore().domain,
  tenantUserId,
  query = {},
  errorConfig = {},
  callbacks = {},
}: GetTenantUserSkillTreesArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.tenantUserSkillTrees({ domain, tenantUserId }),
      query,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiFetchTenantUserBacklogSkillTrees = async ({
  domain = useTenantStore().domain,
  tenantUserId,
  query = {},
  errorConfig = {},
  callbacks = {},
}: GetTenantUserBacklogSkillTreesArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.get({
      url: urls.v1.tenantUserBacklogSkillTrees({ domain, tenantUserId }),
      query,
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};

export const apiDeleteSkillTree = async ({
  domain = useTenantStore().domain,
  skillTreeCode,
  errorConfig = {},
  callbacks = {},
}: DeleteSkillTreeArgsType) => {
  const impl = async () => {
    const { client } = useClient('auth');
    const res = await client.delete({
      url: urls.v1.skillTrees({ domain, skillTreeCode }),
    });
    return res;
  };

  return withTryCatch({ impl, errorConfig, callbacks });
};
